// src/utils/cloudStorage.ts

/**
 * 🔐 CloudStorage API wrapper
 * Использует Telegram CloudStorage когда доступен,
 * fallback на localStorage для dev-режима или при ошибках
 */

import type { WebApp } from '@twa-dev/types';

/** Локальный тип CloudStorage — только для этого файла (без глобального мерджа) */
interface TelegramCloudStorage {
  setItem(key: string, value: string, callback?: (error: Error | null, success?: boolean) => void): void;
  getItem(key: string, callback: (error: Error | null, value?: string | null) => void): void;
  getItems(keys: string[], callback: (error: Error | null, values?: Record<string, string>) => void): void;
  removeItem(key: string, callback?: (error: Error | null, success?: boolean) => void): void;
  removeItems(keys: string[], callback?: (error: Error | null, success?: boolean) => void): void;
  getKeys(callback: (error: Error | null, keys?: string[]) => void): void;
}

/** Удобный псевдотип для доступа к CloudStorage без изменения глобального WebApp */
type WebAppWithCloudStorage = WebApp & {
  CloudStorage?: TelegramCloudStorage;
};

// DEV-флаг: поддерживаем и CRA, и Vite
const DEV_MODE =
  (typeof process !== 'undefined' && process?.env?.REACT_APP_DEV_MODE === 'true') ||
  (typeof import.meta !== 'undefined' && (import.meta as any)?.env?.VITE_DEV_MODE === 'true') ||
  false;

/** Проверяет доступность CloudStorage */
const isCloudStorageAvailable = (): boolean => {
  return !!(window.Telegram?.WebApp && (window.Telegram.WebApp as WebAppWithCloudStorage).CloudStorage);
};

// Запись оставляем короче, а чтение увеличиваем, потому что на мобильных
// WebView ответы CloudStorage приходят заметно позже и раньше времени уходили
// в fallback на пустой localStorage (отсюда серые статусы на мобильном).
const CLOUDSTORAGE_WRITE_TIMEOUT = 3000; // 3 секунды
const CLOUDSTORAGE_READ_TIMEOUT = 8000; // 8 секунд

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`CloudStorage timeout после ${timeoutMs}ms`)), timeoutMs)
    ),
  ]);
}

export const CloudStorage = {
  /** Сохранить значение */
  async setItem(key: string, value: string): Promise<void> {
    const operation = new Promise<void>((resolve, reject) => {
      const webApp = window.Telegram?.WebApp as WebAppWithCloudStorage | undefined;

      if (webApp?.CloudStorage) {
        webApp.CloudStorage.setItem(key, value, (error) => {
          if (error) {
            console.warn('⚠️ CloudStorage.setItem error, fallback to localStorage:', error);
            try {
              localStorage.setItem(key, value);
              resolve();
            } catch (localError) {
              reject(localError);
            }
          } else {
            if (DEV_MODE) console.log('✅ CloudStorage.setItem:', key);
            resolve();
          }
        });
      } else {
        try {
          localStorage.setItem(key, value);
          if (DEV_MODE) console.log('🔧 CloudStorage недоступен, localStorage.setItem:', key);
          resolve();
        } catch (error) {
          reject(error);
        }
      }
    });

    // ✅ КРИТИЧНО: Оборачиваем в timeout // ✅ КРИТИЧНО: Оборачиваем в timeout
    try {
      await withTimeout(operation, CLOUDSTORAGE_WRITE_TIMEOUT);
    } catch (timeoutError) {
      console.warn('⚠️ CloudStorage.setItem timeout, fallback to localStorage:', timeoutError);
      localStorage.setItem(key, value);
    }
  },

  /** Получить значение */
  async getItem(key: string): Promise<string | null> {
    const operation = new Promise<string | null>((resolve, reject) => {
      const webApp = window.Telegram?.WebApp as WebAppWithCloudStorage | undefined;

      if (webApp?.CloudStorage) {
        webApp.CloudStorage.getItem(key, (error, value) => {
          if (error) {
            console.warn('⚠️ CloudStorage.getItem error, fallback to localStorage:', error);
            try {
              resolve(localStorage.getItem(key));
            } catch (localError) {
              reject(localError);
            }
          } else {
            if (DEV_MODE && value) console.log('✅ CloudStorage.getItem:', key);
            resolve(value ?? null);
          }
        });
      } else {
        try {
          if (DEV_MODE) console.log('🔧 CloudStorage недоступен, localStorage.getItem:', key);
          resolve(localStorage.getItem(key));
        } catch (error) {
          reject(error);
        }
      }
    });

    // ✅ КРИТИЧНО: Оборачиваем в timeout
    try {
      return await withTimeout(operation, CLOUDSTORAGE_READ_TIMEOUT);
    } catch (timeoutError) {
      console.warn('⚠️ CloudStorage.getItem timeout, fallback to localStorage:', timeoutError);
      return localStorage.getItem(key);
    }
  },

  /** Получить несколько значений */
  async getItems(keys: string[]): Promise<Record<string, string>> {
    const operation = new Promise<Record<string, string>>((resolve, reject) => {
      const webApp = window.Telegram?.WebApp as WebAppWithCloudStorage | undefined;

      if (webApp?.CloudStorage) {
        webApp.CloudStorage.getItems(keys, (error, values) => {
          if (error) {
            console.warn('⚠️ CloudStorage.getItems error, fallback to localStorage:', error);
            try {
              const result: Record<string, string> = {};
              for (const k of keys) {
                const v = localStorage.getItem(k);
                if (v !== null) result[k] = v;
              }
              resolve(result);
            } catch (localError) {
              reject(localError);
            }
          } else {
            resolve(values ?? {});
          }
        });
      } else {
        try {
          const result: Record<string, string> = {};
          for (const k of keys) {
            const v = localStorage.getItem(k);
            if (v !== null) result[k] = v;
          }
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }
    });

    // ✅ Оборачиваем в timeout
    try {
      return await withTimeout(operation, CLOUDSTORAGE_READ_TIMEOUT);
    } catch (timeoutError) {
      console.warn('⚠️ CloudStorage.getItems timeout, fallback to localStorage:', timeoutError);
      const result: Record<string, string> = {};
      for (const k of keys) {
        const v = localStorage.getItem(k);
        if (v !== null) result[k] = v;
      }
      return result;
    }
  },

  /** Удалить значение */
  async removeItem(key: string): Promise<void> {
    const operation = new Promise<void>((resolve, reject) => {
      const webApp = window.Telegram?.WebApp as WebAppWithCloudStorage | undefined;

      if (webApp?.CloudStorage) {
        webApp.CloudStorage.removeItem(key, (error) => {
          if (error) {
            console.warn('⚠️ CloudStorage.removeItem error, fallback to localStorage:', error);
            try {
              localStorage.removeItem(key);
              resolve();
            } catch (localError) {
              reject(localError);
            }
          } else {
            if (DEV_MODE) console.log('✅ CloudStorage.removeItem:', key);
            resolve();
          }
        });
      } else {
        try {
          localStorage.removeItem(key);
          resolve();
        } catch (error) {
          reject(error);
        }
      }
    });

    // ✅ КРИТИЧНО: Оборачиваем в timeout (важно для submit анкеты!)
    try {
      return await withTimeout(operation, CLOUDSTORAGE_READ_TIMEOUT);
    } catch (timeoutError) {
      console.warn('⚠️ CloudStorage.removeItem timeout, fallback to localStorage:', timeoutError);
      localStorage.removeItem(key);
    }
  },

  /** Удалить несколько значений */
  async removeItems(keys: string[]): Promise<void> {
    return new Promise((resolve, reject) => {
      const webApp = window.Telegram?.WebApp as WebAppWithCloudStorage | undefined;

      if (webApp?.CloudStorage) {
        webApp.CloudStorage.removeItems(keys, (error) => {
          if (error) {
            console.warn('⚠️ CloudStorage.removeItems error, fallback to localStorage:', error);
            try {
              for (const k of keys) localStorage.removeItem(k);
              resolve();
            } catch (localError) {
              reject(localError);
            }
          } else {
            if (DEV_MODE) console.log('✅ CloudStorage.removeItems:', keys);
            resolve();
          }
        });
      } else {
        try {
          for (const k of keys) localStorage.removeItem(k);
          resolve();
        } catch (error) {
          reject(error);
        }
      }
    });
  },

  /** Получить все ключи */
  async getKeys(): Promise<string[]> {
    return new Promise((resolve, reject) => {
      const webApp = window.Telegram?.WebApp as WebAppWithCloudStorage | undefined;

      if (webApp?.CloudStorage) {
        webApp.CloudStorage.getKeys((error, keys) => {
          if (error) {
            console.warn('⚠️ CloudStorage.getKeys error, fallback to localStorage:', error);
            try {
              resolve(Object.keys(localStorage));
            } catch (localError) {
              reject(localError);
            }
          } else {
            resolve(keys ??[]);
          }
        });
      } else {
        try {
          resolve(Object.keys(localStorage));
        } catch (error) {
          reject(error);
        }
      }
    });
  },

  /** Проверка доступности CloudStorage */
  isAvailable(): boolean {
    return isCloudStorageAvailable();
  },
};

/** Хелпер для миграции с localStorage на CloudStorage */
export const migrateToCloudStorage = async (keys: string[]): Promise<void> => {
  if (!CloudStorage.isAvailable()) {
    console.log('⏭️ CloudStorage недоступен, миграция пропущена');
    return;
  }

  console.log('🔄 Начинаем миграцию на CloudStorage...');
  for (const key of keys) {
    try {
      const value = localStorage.getItem(key);
      if (value !== null) {
        await CloudStorage.setItem(key, value);
        console.log(`✅ Мигрировано: ${key}`);
      }
    } catch (error) {
      console.error(`❌ Ошибка миграции ${key}:`, error);
    }
  }
  console.log('✅ Миграция завершена');
};

// ========== TASK 2: МЕТОДЫ ДЛЯ APPLICATIONS ==========

export interface Application {
  date: string;
  marked: boolean;
  feedback?: 'good' | 'neutral' | 'discomfort';
  note?: string;
  createdAt: string;
}

const APPLICATIONS_KEY = 'retinol_applications';

/**
 * ✅ FIX: Транзакционная синхронизация applications (read-modify-write)
 * Предотвращает race conditions при одновременной записи с нескольких устройств
 */
export const syncApplicationsToCloud = async (applications: Application[]): Promise<void> => {
  const maxRetries = 2;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      // 1. READ: читаем текущее состояние из CloudStorage
      const currentRaw = await CloudStorage.getItem(APPLICATIONS_KEY);
      const currentApplications: Application[] = currentRaw ? JSON.parse(currentRaw) : [];

      // 2. MODIFY: мержим с новыми данными (приоритет более свежему createdAt)
      const mergedApplications = mergeApplications(currentApplications, applications);

      // 3. WRITE: сохраняем смерженный результат
      const serialized = JSON.stringify(mergedApplications);
      await CloudStorage.setItem(APPLICATIONS_KEY, serialized);

      if (DEV_MODE) {
        console.log(
          `✅ Applications синхронизированы транзакционно (попытка ${attempt + 1}):`,
          `${currentApplications.length} текущих + ${applications.length} новых → ${mergedApplications.length} итого`
        );
      }
      return; // Успех - выходим
    } catch (error) {
      console.error(`❌ Ошибка синхронизации applications (попытка ${attempt + 1}):`, error);

      if (attempt === maxRetries - 1) {
        // Последняя попытка - fallback на localStorage
        try {
          const serialized = JSON.stringify(applications);
          localStorage.setItem(APPLICATIONS_KEY, serialized);
          console.warn('⚠️ Fallback на localStorage для applications');
        } catch (fallbackError) {
          console.error('❌ Ошибка fallback на localStorage:', fallbackError);
          throw error; // Пробрасываем исходную ошибку
        }
      }
      // Не делаем задержку между попытками, т.к. это может замедлить UX
    }
  }
};

/**
 * Загружает applications из CloudStorage
 */
export const getApplicationsFromCloud = async (): Promise<Application[]> => {
  try {
    const serialized = await CloudStorage.getItem(APPLICATIONS_KEY);
    if (!serialized) {
      if (DEV_MODE) console.log('ℹ️ CloudStorage: applications отсутствуют');
      return[];
    }
    const applications: Application[] = JSON.parse(serialized);
    if (DEV_MODE) {
      console.log('✅ Applications загружены из CloudStorage:', applications.length);
    }
    return applications;
  } catch (error) {
    console.error('❌ Ошибка загрузки applications из CloudStorage:', error);
    return[];
  }
};

/**
 * Merge стратегия: приоритет более свежему createdAt
 */
export const mergeApplications = (
  local: Application[],
  cloud: Application[]
): Application[] => {
  const merged = new Map<string, Application>();

  // Сначала добавляем все локальные
  for (const app of local) {
    merged.set(app.date, app);
  }

  // Затем merge с облачными (приоритет более свежему)
  for (const app of cloud) {
    const existing = merged.get(app.date);
    if (!existing || new Date(app.createdAt) > new Date(existing.createdAt)) {
      merged.set(app.date, app);
    }
  }

  return Array.from(merged.values()).sort((a, b) => a.date.localeCompare(b.date));
};

/**
 * ✅ ВАРИАНТ A: Очищает applications из CloudStorage при reset
 */
export const clearApplicationsFromCloud = async (): Promise<void> => {
  try {
    await CloudStorage.removeItem(APPLICATIONS_KEY);
    console.log('🧹 Applications очищены из CloudStorage');
  } catch (error) {
    console.error('❌ Ошибка очистки applications из CloudStorage:', error);
    throw error;
  }
};

// ========== TIMEZONE SYNC ==========

const TIMEZONE_KEY = 'user_timezone';

/**
 * Сохраняет timezone в CloudStorage для синхронизации между устройствами
 */
export const syncTimezoneToCloud = async (timezone: string): Promise<void> => {
  try {
    await CloudStorage.setItem(TIMEZONE_KEY, timezone);
    console.log('✅ Timezone синхронизирован в CloudStorage:', timezone);
  } catch (error) {
    console.error('❌ Ошибка синхронизации timezone в CloudStorage:', error);
    throw error;
  }
};

/**
 * Загружает timezone из CloudStorage
 */
export const getTimezoneFromCloud = async (): Promise<string | null> => {
  try {
    const timezone = await CloudStorage.getItem(TIMEZONE_KEY);
    if (timezone) {
      console.log('✅ Timezone загружен из CloudStorage:', timezone);
    }
    return timezone;
  } catch (error) {
    console.error('❌ Ошибка загрузки timezone из CloudStorage:', error);
    return null;
  }
};

// ========== NOTIFICATIONS SYNC ==========

const NOTIFICATIONS_KEY = 'user_notifications_enabled';

/**
 * Сохраняет состояние уведомлений в CloudStorage для синхронизации между устройствами
 */
export const syncNotificationsToCloud = async (enabled: boolean): Promise<void> => {
  try {
    await CloudStorage.setItem(NOTIFICATIONS_KEY, enabled.toString());
    console.log('✅ Notifications синхронизированы в CloudStorage:', enabled);
  } catch (error) {
    console.error('❌ Ошибка синхронизации notifications в CloudStorage:', error);
    throw error;
  }
};

/**
 * Загружает состояние уведомлений из CloudStorage
 */
export const getNotificationsFromCloud = async (): Promise<boolean | null> => {
  try {
    const value = await CloudStorage.getItem(NOTIFICATIONS_KEY);
    if (value !== null) {
      const enabled = value === 'true';
      console.log('✅ Notifications загружены из CloudStorage:', enabled);
      return enabled;
    }
    return null;
  } catch (error) {
    console.error('❌ Ошибка загрузки notifications из CloudStorage:', error);
    return null;
  }
};
